import { z } from "zod";

// ---------------------------------------------------------------------------
// Sales Insight feedback (per assistant message thumbs up/down + reasons)
//
// Stored at:
//   Businesses/{businessId}/salesInsightThreads/{threadId}/feedback/{messageId}
//
// Keying by `messageId` makes the doc upsertable: re-clicking the same vote
// clears it, switching from up to down updates the same row, adding a comment
// after a 👎 merges into the existing doc.
// ---------------------------------------------------------------------------

export const FeedbackVote = z.enum(["up", "down"]);
export type FeedbackVote = z.infer<typeof FeedbackVote>;

export const FeedbackReasonCode = z.enum([
  "incorrect", // factually wrong about the data
  "wrong_person", // mixed up team members
  "vague", // didn't say anything actionable
  "format", // bad structure / too long / too short
  "unsafe", // reveals something it shouldn't / unfair
  "other",
]);
export type FeedbackReasonCode = z.infer<typeof FeedbackReasonCode>;

export const FeedbackScope = z.enum(["business", "team", "member"]);
export type FeedbackScope = z.infer<typeof FeedbackScope>;

export const SubmitFeedbackInput = z.object({
  businessId: z.string().min(1),
  threadId: z.string().min(1),
  messageId: z.string().min(1),
  vote: FeedbackVote,
  reasonCodes: z.array(FeedbackReasonCode).max(5).optional().default([]),
  comment: z.string().max(2000).nullish(),

  // Bucketing dimensions used for analytics.
  topicKey: z.string().min(1),
  scope: FeedbackScope,
  salesTeamId: z.string().min(1).nullish(),
  memberId: z.string().min(1).nullish(),

  // Captured from the assistant message's metadata (emitted by the chat
  // handler at stream start). Nullable because messages predating the
  // metadata rollout, or rehydrated from an older thread, won't have these.
  promptVersion: z.number().int().nonnegative().nullish(),
  modelId: z.string().min(1).nullish(),
  contextFingerprint: z.string().min(1).nullish(),
  /**
   * The LangSmith run behind the answer (message metadata `runId`), so the
   * vote can be attached to the trace (GRA-66). Absent on older messages.
   */
  runId: z.string().min(1).max(64).nullish(),
});
export type SubmitFeedbackInput = z.infer<typeof SubmitFeedbackInput>;

export const ClearMessageFeedbackInput = z.object({
  businessId: z.string().min(1),
  threadId: z.string().min(1),
  messageId: z.string().min(1),
});
export type ClearMessageFeedbackInput = z.infer<
  typeof ClearMessageFeedbackInput
>;

export const ListMessageFeedbackInput = z.object({
  businessId: z.string().min(1),
  threadId: z.string().min(1),
});
export type ListMessageFeedbackInput = z.infer<
  typeof ListMessageFeedbackInput
>;

export interface FeedbackRecord {
  messageId: string;
  threadId: string;
  businessId: string;
  vote: FeedbackVote;
  reasonCodes: FeedbackReasonCode[];
  comment: string | null;
  topicKey: string;
  scope: FeedbackScope;
  salesTeamId: string | null;
  memberId: string | null;
  promptVersion: number | null;
  modelId: string | null;
  contextFingerprint: string | null;
  userId: string;
  createdAt: string | null;
  updatedAt: string | null;
}
